import React, { useState } from 'react';

export default function BackupCodesDisplay({ codes, onAcknowledge }) {
  const [downloaded, setDownloaded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleDownload = () => {
    const content = `TriRail StablePay - Backup Codes\n\nGenerated: ${new Date()?.toLocaleString()}\n\nIMPORTANT: Store these codes in a secure location.\nEach code can only be used once.\n\n${codes?.join('\n')}\n\nIf you lose access to your authentication method, you can use these codes to sign in.`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trirail-backup-codes-${Date.now()}.txt`;
    a?.click();
    URL.revokeObjectURL(url);
    setDownloaded(true);
  };

  const handleCopy = () => {
    navigator.clipboard?.writeText(codes?.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow?.document?.write('<html><head><title>Backup Codes</title>');
    printWindow?.document?.write('<style>body { font-family: monospace; padding: 20px; } h1 { font-size: 18px; } .code { margin: 10px 0; font-size: 16px; }</style>');
    printWindow?.document?.write('</head><body>');
    printWindow?.document?.write('<h1>TriRail StablePay - Backup Codes</h1>');
    printWindow?.document?.write(`<p>Generated: ${new Date()?.toLocaleString()}</p>`);
    printWindow?.document?.write('<p><strong>IMPORTANT: Store these codes in a secure location. Each code can only be used once.</strong></p>');
    codes?.forEach(code => {
      printWindow?.document?.write(`<div class="code">${code}</div>`);
    });
    printWindow?.document?.write('</body></html>');
    printWindow?.document?.close();
    printWindow?.print();
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Save Your Backup Codes</h2>
      <p className="text-gray-600 mb-6">These codes can be used to access your account if you lose your authentication device</p>
      {/* Warning Banner */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <div className="flex">
          <svg className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-sm font-medium text-red-800">Critical: Save These Codes Now</p>
            <p className="text-sm text-red-700 mt-1">
              You will not be able to see these codes again. Each code can only be used once.
            </p>
          </div>
        </div>
      </div>
      {/* Backup Codes Grid */}
      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <div className="grid grid-cols-2 gap-4">
          {codes?.map((code, index) => (
            <div
              key={index}
              className="bg-white px-4 py-3 rounded border border-gray-300 font-mono text-center text-lg font-semibold"
            >
              {code}
            </div>
          ))}
        </div>
      </div>
      {/* Action Buttons */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <button
          onClick={handleDownload}
          className="flex items-center justify-center px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download
        </button>

        <button
          onClick={handleCopy}
          className="flex items-center justify-center px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          {copied ? 'Copied!' : 'Copy'}
        </button>

        <button
          onClick={handlePrint}
          className="flex items-center justify-center px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Print
        </button>
      </div>
      {/* Storage Recommendations */}
      <div className="bg-blue-50 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-900 mb-2">Secure Storage Recommendations:</h3>
        <ul className="space-y-2 text-blue-800 text-sm">
          <li className="flex items-start">
            <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Store in a password manager (recommended)</span>
          </li>
          <li className="flex items-start">
            <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Print and store in a secure physical location</span>
          </li>
          <li className="flex items-start">
            <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Keep separate from your authentication device</span>
          </li>
          <li className="flex items-start">
            <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Never share these codes with anyone</span>
          </li>
        </ul>
      </div>
      {/* Confirmation Checkbox */}
      <div className="mb-6">
        <label className="flex items-start">
          <input
            type="checkbox"
            checked={downloaded}
            onChange={(e) => setDownloaded(e?.target?.checked)}
            className="mt-1 mr-3 h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <span className="text-gray-700">
            I have saved these backup codes in a secure location and understand they cannot be recovered if lost
          </span>
        </label>
      </div>
      {/* Continue Button */}
      <button
        onClick={onAcknowledge}
        disabled={!downloaded}
        className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        Continue to Complete Setup
      </button>
    </div>
  );
}